// AB#2130 — Overview tab (index child of /tenants/$slug, eagerly loaded). Carries the metadata
// grid that used to sit inside the old header card, plus a read-only companies summary; the full
// companies table lives on the Companies tab. Re-issues the layout's ['tenant', slug] query —
// same key, so it dedupes against the app-global cache.
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'

export function OverviewTab() {
    const { slug } = useParams({ from: '/tenants/$slug' })
    const queryClient = useQueryClient()

    const { data: tenant } = useQuery({
        queryKey: ['tenant', slug],
        queryFn: () => centralApi.getTenant(slug),
    })

    // AB#2457 — platform Google key grants: operator-controlled here, never by the tenant. The
    // tenant's Integrations page shows the effect read-only.
    const platformKeysMutation = useMutation({
        mutationFn: (grants: { maps: boolean; places: boolean; mailbox: boolean; documentIntelligence: boolean }) =>
            centralApi.setTenantPlatformKeys(slug, grants.maps, grants.places, grants.mailbox, grants.documentIntelligence),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant', slug] }),
    })

    // The layout only renders the Outlet once the tenant query has resolved; this guard covers
    // the transient window before the shared cache entry is visible.
    if (!tenant) return null

    return (
        <div className="space-y-8">
            {/* Metadata grid (moved out of the old header card) */}
            <div className="bg-white rounded-lg border border-neutral-200 p-6">
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    {[
                        ['Mode', tenant.provisioningMode],
                        ['Admin email', tenant.adminEmail],
                        ['Created', new Date(tenant.createdAt).toLocaleDateString()],
                    ].map(([label, value]) => (
                        <div key={label}>
                            <dt className="text-neutral-500">{label}</dt>
                            <dd className="font-medium text-neutral-900 mt-0.5">{value}</dd>
                        </div>
                    ))}
                </dl>
            </div>

            {/* Companies summary — read-only; manage them on the Companies tab */}
            <section>
                <h2 className="text-lg font-medium text-neutral-900 mb-3">Companies</h2>
                <div className="bg-white rounded-lg border border-neutral-200 p-6">
                    {tenant.companies.length === 0 ? (
                        <p className="text-sm text-neutral-400">No companies.</p>
                    ) : (
                        <>
                            <p className="text-sm text-neutral-500">
                                {tenant.companies.length} {tenant.companies.length === 1 ? 'company' : 'companies'}
                            </p>
                            <ul className="mt-2 space-y-1">
                                {tenant.companies.map(c => (
                                    <li key={c.companyId} className="text-sm font-medium text-neutral-900">
                                        {c.displayName}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </section>

            {/* AB#2457 — platform Google key grants (the AB#1626 licensing-toggle shape). */}
            <section>
                <h2 className="text-lg font-medium text-neutral-900 mb-3">Platform integrations</h2>
                <div className="bg-white rounded-lg border border-neutral-200 p-6 space-y-4">
                    <p className="text-sm text-neutral-500">
                        Grant this tenant platform-provided capabilities instead of requiring their own
                        configuration. The tenant sees the effect on its own settings pages but never these
                        toggles or any credential.
                    </p>
                    {[
                        {
                            label: 'Use the platform Maps key',
                            hint: 'Map components load with the platform client key.',
                            checked: tenant.usePlatformMapsKey,
                            next: () => ({ maps: !tenant.usePlatformMapsKey, places: tenant.usePlatformPlacesKey, mailbox: tenant.usePlatformMailbox, documentIntelligence: tenant.usePlatformDocumentIntelligence }),
                        },
                        {
                            label: 'Use the platform address-lookup key',
                            hint: 'Address search/geocoding runs on the platform Places key.',
                            checked: tenant.usePlatformPlacesKey,
                            next: () => ({ maps: tenant.usePlatformMapsKey, places: !tenant.usePlatformPlacesKey, mailbox: tenant.usePlatformMailbox, documentIntelligence: tenant.usePlatformDocumentIntelligence }),
                        },
                        {
                            // AB#2933 — lending the platform's sending reputation is an operator decision;
                            // AB#2920 briefly shipped it as a tenant-side choice, which was the wrong trust model.
                            label: 'Use the platform mailbox',
                            hint: 'Companies with no mailbox of their own send business email through the platform mailbox (configured under SMTP).',
                            checked: tenant.usePlatformMailbox,
                            next: () => ({ maps: tenant.usePlatformMapsKey, places: tenant.usePlatformPlacesKey, mailbox: !tenant.usePlatformMailbox, documentIntelligence: tenant.usePlatformDocumentIntelligence }),
                        },
                        {
                            // AB#3770 — the one grant that defaults ON: the platform owns the Azure account, so a
                            // tenant needs nothing of its own; a connection the tenant registers itself still wins.
                            label: 'Use the platform handwriting recognition',
                            hint: 'Photographed notes on CRM records are transcribed through the platform Azure Document Intelligence account (configured under SMTP & keys). A tenant with its own azure-document-intelligence connection uses that instead.',
                            checked: tenant.usePlatformDocumentIntelligence,
                            next: () => ({ maps: tenant.usePlatformMapsKey, places: tenant.usePlatformPlacesKey, mailbox: tenant.usePlatformMailbox, documentIntelligence: !tenant.usePlatformDocumentIntelligence }),
                        },
                    ].map(row => (
                        <label key={row.label} className="flex flex-wrap items-center justify-between gap-4 text-sm text-neutral-700 select-none">
                            <span>
                                <span className="font-medium text-neutral-900">{row.label}</span>
                                <span className="block text-xs text-neutral-500">{row.hint}</span>
                            </span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={row.checked}
                                disabled={platformKeysMutation.isPending}
                                onClick={() => platformKeysMutation.mutate(row.next())}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                                    row.checked ? 'bg-green-500' : 'bg-neutral-300'
                                }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        row.checked ? 'translate-x-4' : 'translate-x-0.5'
                                    }`}
                                />
                            </button>
                        </label>
                    ))}
                </div>
            </section>
        </div>
    )
}
